<template>
  <v-list>
    <v-list-item class="px-0">
      <v-radio-group
        ref="radioGroupScrollable"
        v-model="selectedParameter"
        class="radio-buttons scrollable"
        hide-details
        :mandatory="false"
        @update:model-value="parameterChange"
      >
        <div v-for="(item, index) in parameterOptions" :key="item.value">
          <v-radio
            :value="item.value"
            :label="item.label"
            color="primary"
            :data-cy="`measurementParameter${index}`"
          />
        </div>
      </v-radio-group>
    </v-list-item>
  </v-list>
</template>

<script lang="ts">
import { defineComponent, nextTick, type PropType } from 'vue'
import type { VRadioGroup } from 'vuetify/components'
import type { WidgetSettings } from '@/types/dashboard'
import { getWidgetMeasurementOptions, type WidgetMeasurementOption } from '@/helpers/widgets/helper'
import { saveWidgetSettings } from '@/services/dashboard'
import { useMeasurementOptions } from '@/composables/widgets/useMeasurementOptions'

export default defineComponent({
  name: 'MeasurementOptions',
  emits: ['parameterChange'],
  props: {
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    passedinParameter: {
      type: Number,
      default: null
    },
    widgetId: {
      type: String,
      default: ''
    },
    filterMeasurementStatistics: {
      type: Boolean,
      default: false
    },
    defaultParameter: {
      type: Number,
      default: null
    },
    settings: {
      type: Object as PropType<WidgetSettings | null>,
      default: null
    }
  },
  setup(props) {
    return useMeasurementOptions(props)
  },
  watch: {
    parameterOptions(): void {
      // determine the index of selected element plus 1 which corresponds to the padding
      const selectedParameterIndex =
        this.parameterOptions.findIndex((parameter) => parameter.value === this.selectedParameter) +
        1
      if (selectedParameterIndex > 0) {
        this.setScroll(selectedParameterIndex)
      }
    },
    selectedParameter(selectedParameter: number | null): void {
      if (typeof selectedParameter === 'number' && this.widgetId) {
        saveWidgetSettings(this.widgetId, { selectedParameter })
      }
    }
  },
  computed: {
    parameterOptions(): WidgetMeasurementOption[] {
      return getWidgetMeasurementOptions(this.$t.bind(this), this.availableMeasurements)
    }
  },
  methods: {
    parameterChange(): void {
      this.$emit('parameterChange', this.selectedParameter)
    },
    async setScroll(selectedParameterIndex: number): Promise<void> {
      await nextTick()
      setTimeout(() => {
        const radioGroup = this.$refs.radioGroupScrollable as VRadioGroup
        const radioGroupElement = radioGroup?.$el as HTMLElement
        const radioGroupHeight = radioGroupElement?.clientHeight || 0

        if (radioGroupElement && radioGroupHeight > 0) {
          // calculate the amount of visible items based on the radio group height to
          // determine the scrollTop value assigned to the radio group
          const itemHeight = 24
          const visibleItems = Math.round(radioGroupHeight / itemHeight) - 1
          const visibleSection = Math.ceil(selectedParameterIndex / visibleItems)
          radioGroupElement.scrollTop = radioGroupHeight * (visibleSection - 1)
        }
      }, 100)
    }
  }
})
</script>
<style lang="scss" scoped>
.v-list {
  padding: 0;
}

.v-list-item {
  padding: 0;
}

.scrollable {
  overflow: auto;
  max-height: 380px;
  padding: 8px;
  margin-top: 0;
}

:deep(.measurementParameter) label {
  padding-left: 4px;
}
</style>
